/**
 * 语言状态管理
 */
import { defineStore } from 'pinia'
import storage from '@/common/storage'
import i18n from '@/i18n'

// 支持的语言列表
const SUPPORTED_LANGUAGES = [
  { code: 'zh-CN', name: '中文' },
  { code: 'en-US', name: 'English' },
  { code: 'ja-JP', name: '日本語' },
  { code: 'ko-KR', name: '한국어' },
  { code: 'fr-FR', name: 'Français' },
  { code: 'de-DE', name: 'Deutsch' },
  { code: 'es-ES', name: 'Español' },
  { code: 'ar-SA', name: 'العربية' },
  // { code: 'it-IT', name: 'Italiano' },
  // { code: 'pt-PT', name: 'Português' },
  // { code: 'ru-RU', name: 'Русский' },
  // { code: 'th-TH', name: 'ไทย' },
  // { code: 'vi-VN', name: 'Tiếng Việt' }
]

// 简化的语言映射 (用于后端API)
const LANGUAGE_MAP = {
  'zh-CN': 'zh',
  'en-US': 'en',
  'ja-JP': 'ja',
  'ko-KR': 'ko',
  'fr-FR': 'fr',
  'de-DE': 'de',
  'es-ES': 'es',
  'ar-SA': 'ar'
  // 'it-IT': 'it',
  // 'pt-PT': 'pt',
  // 'ru-RU': 'ru',
  // 'th-TH': 'th',
  // 'vi-VN': 'vi'
}

export const useLanguageStore = defineStore('language', {
  state: () => ({
    // UI语言 (i18n)
    uiLanguage: storage.get('ui_language', null),
    
    // 目标语言 (翻译目标)
    targetLanguage: storage.get('target_language', null),
    
    // 源语言 (菜单原始语言)
    sourceLanguage: storage.get('source_language', null),
    
    // 是否已初始化
    initialized: false
  }),

  getters: {
    /**
     * 获取当前UI语言
     */
    currentUiLanguage: (state) => state.uiLanguage || 'zh-CN',
    
    /**
     * 获取当前目标语言
     */
    currentTargetLanguage: (state) => state.targetLanguage || state.uiLanguage || 'zh-CN',
    
    /**
     * 获取当前源语言
     */
    currentSourceLanguage: (state) => state.sourceLanguage || 'auto',
    
    /**
     * 获取语言列表
     */
    languages: () => SUPPORTED_LANGUAGES,
    
    /**
     * 获取当前UI语言名称
     */
    currentUiLanguageName: (state) => {
      const lang = SUPPORTED_LANGUAGES.find(l => l.code === state.uiLanguage)
      return lang ? lang.name : '中文'
    },
    
    /**
     * 获取当前目标语言名称
     */
    currentTargetLanguageName: (state) => {
      const lang = SUPPORTED_LANGUAGES.find(l => l.code === state.targetLanguage)
      return lang ? lang.name : '中文'
    },
    
    /**
     * 获取API格式的目标语言代码
     */
    apiTargetLanguage: (state) => {
      const targetLang = state.targetLanguage || state.uiLanguage || 'zh-CN'
      return LANGUAGE_MAP[targetLang] || 'zh'
    },
    
    /**
     * 获取API格式的源语言代码
     */
    apiSourceLanguage: (state) => {
      if (!state.sourceLanguage || state.sourceLanguage === 'auto') {
        return 'auto'
      }
      return LANGUAGE_MAP[state.sourceLanguage] || state.sourceLanguage
    }
  },

  actions: {
    /**
     * 初始化语言设置
     */
    initLanguage() {
      if (this.initialized) return
      
      // 先检测浏览器语言
      const browserLang = this.detectBrowserLanguage()
      
      // 如果没有保存的UI语言，使用浏览器语言
      if (!this.uiLanguage) {
        this.uiLanguage = browserLang
        storage.set('ui_language', browserLang)
      }
      
      // 如果没有保存的目标语言，使用浏览器语言（或UI语言）
      if (!this.targetLanguage) {
        this.targetLanguage = browserLang
        storage.set('target_language', browserLang)
      }
      
      // 应用UI语言到i18n
      if (i18n.global) {
        i18n.global.locale.value = this.uiLanguage
      }
      
      this.initialized = true
    },
    
    /**
     * 检测浏览器语言
     */
    detectBrowserLanguage() {
      // 获取浏览器语言
      const browserLang = navigator.language || navigator.userLanguage || 'zh-CN'
      
      // 标准化语言代码
      const normalizedLang = this.normalizeLanguageCode(browserLang)
      
      // 检查是否支持
      const isSupported = SUPPORTED_LANGUAGES.some(l => l.code === normalizedLang)
      
      return isSupported ? normalizedLang : 'zh-CN'
    },
    
    /**
     * 标准化语言代码
     */
    normalizeLanguageCode(code) {
      if (!code) return 'zh-CN'
      
      // 转换为标准格式 (xx-XX)
      const parts = code.split(/[-_]/)
      if (parts.length === 1) {
        // 只有语言代码，添加默认地区
        const langMap = {
          'zh': 'zh-CN',
          'en': 'en-US',
          'ja': 'ja-JP',
          'ko': 'ko-KR',
          'fr': 'fr-FR',
          'de': 'de-DE',
          'es': 'es-ES',
          'ar': 'ar-SA',
          'it': 'it-IT',
          'pt': 'pt-PT',
          'ru': 'ru-RU',
          'th': 'th-TH',
          'vi': 'vi-VN'
        }
        return langMap[parts[0].toLowerCase()] || 'zh-CN'
      }
      
      // 格式化为 xx-XX
      const lang = parts[0].toLowerCase()
      const region = parts[1].toUpperCase()
      
      // 处理特殊情况
      if (lang === 'zh') {
        // 简体/繁体中文
        if (region === 'TW' || region === 'HK' || region === 'MO') {
          return 'zh-CN' // 暂时都映射到简体中文
        }
        return 'zh-CN'
      }
      
      return `${lang}-${region}`
    },
    
    /**
     * 设置UI语言
     */
    setUiLanguage(language) {
      const normalizedLang = this.normalizeLanguageCode(language)
      this.uiLanguage = normalizedLang
      storage.set('ui_language', normalizedLang)
      
      // 同时更新i18n
      if (i18n.global) {
        i18n.global.locale.value = normalizedLang
      }
      
      // 如果目标语言未设置，同步更新
      if (!this.targetLanguage) {
        this.setTargetLanguage(normalizedLang)
      }
    },
    
    /**
     * 设置目标语言
     */
    setTargetLanguage(language) {
      const normalizedLang = this.normalizeLanguageCode(language)
      this.targetLanguage = normalizedLang
      storage.set('target_language', normalizedLang)
    },
    
    /**
     * 设置源语言
     */
    setSourceLanguage(language) {
      this.sourceLanguage = language
      storage.set('source_language', language)
    },
    
    /**
     * 清除语言设置
     */
    clearLanguageSettings() {
      this.uiLanguage = null
      this.targetLanguage = null
      this.sourceLanguage = null
      storage.remove('ui_language')
      storage.remove('target_language')
      storage.remove('source_language')
      this.initialized = false
    }
  },
  
  persist: {
    key: 'language',
    storage: localStorage,
    pick: ['uiLanguage', 'targetLanguage', 'sourceLanguage']
  }
})
